import React, { useState, useEffect, useRef } from 'react';
import {
  Camera, Plus, Play, Square, Upload, Send, Settings, Shield,
  AlertTriangle, Eye, Video, Users, Mail, Phone, MapPin, Clock,
  Monitor, Wifi, WifiOff, PlayCircle, StopCircle, Download,
  Bell, CheckCircle, XCircle, AlertCircle, Info, Calendar, Trash2,
  UserPlus, Link, Youtube, Loader, Car
} from 'lucide-react';
import Hls from 'hls.js';

// Supabase imports
import { supabase, sha256 } from './supabaseClient';

// OpenRouter Configuration (Gemini 2.5 Pro via OpenRouter)
const OPENROUTER_API_KEY = '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';
const OPENROUTER_MODEL = 'google/gemini-2.5-pro';

// BulkSMS Configuration
const BULKSMS_CONFIG = {
  username: 'w_ndlela',
  password: 'L0ndek@123',
  apiUrl: 'https://api.bulksms.com/v1/messages'
};

// Enhanced Security Analysis Prompt
const SECURITY_PROMPT = `You are a professional security analyst. Analyze this video ONLY for actual security incidents and threats.

CRITICAL INSTRUCTIONS:
- ONLY report actual security incidents, suspicious activities, or threats
- DO NOT describe normal landscapes, static scenes, or routine activities
- If NO security incidents are detected, return EXACTLY: "THREAT LEVEL: NONE"

THREAT LEVEL CLASSIFICATION:
EXTREME THREAT:
- Active violence, weapons usage, armed robbery
- Firearms, guns, knives, weapons visible or in use
- Explosive devices, active shooter situations
- Life-threatening assault, kidnapping, hostage situations

HIGH THREAT:
- Theft, burglary, breaking and entering
- Vehicle theft, hijacking, carjacking  
- Vandalism, property destruction
- Weapons visible but not actively used
- Trespassing in restricted areas
- Drug dealing, criminal activity
- Break-ins, forced entry

MEDIUM THREAT:
- Suspicious loitering, reconnaissance 
- Unauthorized access attempts
- Unusual behavior patterns
- Minor disturbances

LOW THREAT:
- General monitoring alerts
- Minor rule violations

OUTPUT FORMAT (only if threats detected):
**THREAT LEVEL:** [EXTREME/HIGH/MEDIUM/LOW]
**INCIDENT TYPE:** [Concise 2-4 word description]
**TIMESTAMP:** [HH:MM:SS]
**DESCRIPTION:** [Brief factual description]
**SUBJECTS:**
* Count: [Number]
* Descriptions: [Key identifying details]
**RECOMMENDED ACTION:** [Immediate response needed]
**EVIDENCE:** [Key evidence points]

EXAMPLES:
For theft: "THREAT LEVEL: HIGH, INCIDENT TYPE: Theft in Progress"
For weapons: "THREAT LEVEL: EXTREME, INCIDENT TYPE: Armed Individual"
For hijacking: "THREAT LEVEL: HIGH, INCIDENT TYPE: Vehicle Hijacking"
For no incidents: "THREAT LEVEL: NONE"

Be precise and professional. Only flag genuine security concerns.`;

// Enhanced Traffic Infringement Analysis Prompt (South Africa - NRTA 1996)
const TRAFFIC_PROMPT = `**Role:** You are a highly-skilled traffic law enforcement analyst specializing in the National Road Traffic Act (NRTA), 1996 of South Africa and its associated regulations. You are an expert in identifying and classifying traffic violations from video evidence.

**Objective:** Your primary objective is to meticulously review the provided video footage of road traffic. You must detect, document, and classify all observable traffic infringements. Your analysis must be objective, detailed, and legally sound within the South African context.

**Output Format:**
For each infringement you detect, you must provide the following information in a structured format:

*   **Timestamp:** The precise start and end time of the infringement in MM:SS format.
*   **Classification:** Major or Minor.
*   **Vehicle Description:** Make, model, color, and type (e.g., Sedan, Bakkie, Minibus Taxi, Heavy Goods Vehicle).
*   **License Plate:** The full license plate number and province, if clearly legible. If not, state "Not legible."
*   **Infringement Type:** Select from the list of defined infringements below.
*   **Detailed Description:** A concise, factual description of the action that constitutes the infringement.
*   **Justification:**
    1.  **Why it is an infringement:** Briefly explain which rule was broken.
    2.  **Why it is classified as Major/Minor:** Justify your classification based on the risk assessment guidelines provided below.

**Risk Assessment and Classification Guidelines:**

*   **Major Infringement:** An action that creates an immediate and high probability of a serious collision, or shows a blatant disregard for the safety of other road users. This includes actions that force other drivers or pedestrians to take immediate and significant evasive action.
*   **Minor Infringement:** A clear violation of traffic law where the immediate risk of a serious collision is low, due to context such as low speed, low traffic density, or the absence of other road users in the immediate vicinity.

**List of Infringements to Detect (South African Context):**

**1. Junction and Traffic Control Violations:**
*   **Failure to Stop (Red Robot):** Proceeding through an intersection when the traffic light ('robot') is red.
*   **Failure to Stop (Stop Sign):** Not coming to a complete stop before the stop line at a stop sign.
*   **Failure to Yield:** Not giving way at a yield sign, traffic circle, or to pedestrians at a designated crossing.
*   **Illegal Turn:** Making a turn from an incorrect lane or where prohibited by road signs/markings.

**2. Lane Discipline and Overtaking Violations:**
*   **Driving on the Shoulder:** Using the road shoulder to bypass traffic or for any purpose other than an emergency.
*   **Improper Lane Change:** Changing lanes without checking for safety, cutting off another vehicle, or weaving between lanes.
*   **Overtaking on a Solid Barrier Line:** Crossing a solid white or yellow line to overtake.
*   **Unsafe Overtaking:** Overtaking in a dangerous situation (e.g., on a blind rise, on a curve, with insufficient clear road ahead).
*   **Following Too Closely:** Not maintaining a safe following distance from the vehicle ahead.

**3. Driver Compliance and Behavior:**
*   **Use of a Communication Device:** Any driver seen holding, operating, or looking down at a mobile phone or other device while the vehicle is in motion.
*   **Failure to Wear a Seatbelt:** Any front-seat occupant (or any occupant where seatbelts are fitted) not wearing a seatbelt.
*   **Failure to Signal:** Failing to use the vehicle's indicators when turning, changing lanes, or moving off from a stationary position.

**4. Vehicle-Specific and General Violations:**
*   **Reckless or Negligent Driving:** Any pattern of driving that endangers other road users (e.g., excessive speed for the conditions, aggressive tailgating, sudden braking without cause).
*   **Unsafe Minibus Taxi Maneuvers:** Specific attention to minibus taxis stopping suddenly in moving traffic lanes to load/unload passengers, or pulling out into traffic without yielding.
*   **Overloading:** A vehicle (especially a 'bakkie' or truck) that is visibly and dangerously overloaded with goods or passengers.
*   **Obstructed View:** Driver's view is clearly obstructed by items inside or outside the vehicle.

**Handling Uncertainty:**
If an infringement like seatbelt use or mobile phone use is suspected but cannot be definitively confirmed due to video quality, angle, or obstruction, list it under a separate heading: **"Potentially Unverifiable Infringements."** For each, briefly state the suspected infringement and the reason for the uncertainty (e.g., "Poor visibility into the cabin," "Driver's hands obstructed from view").

**Final Summary:**
At the end of your analysis, provide a summary statement explicitly confirming which categories of infringements were checked for but not detected.
**Example:** "No seatbelt, mobile phone use, or overloading infringements were detected in the footage."

If NO infringements are detected at all, return EXACTLY: "TRAFFIC REPORT: NO INFRINGEMENTS DETECTED"

**Begin Analysis.**`;

// ==================== AUTHENTICATION COMPONENTS ====================

const AuthPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEmailPasswordAuth = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (isLogin) {
        // Login with Supabase - verify SHA256 password
        const passwordHash = await sha256(formData.password);

        const { data, error } = await supabase
          .from('viis_users')
          .select('*')
          .eq('email', formData.email)
          .eq('password_hash', passwordHash)
          .single();

        if (error || !data) {
          throw new Error('Invalid email or password');
        }

        onLogin({
          uid: data.id,
          email: data.email,
          displayName: data.name
        });
      } else {
        // Register new user
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (formData.password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }

        const passwordHash = await sha256(formData.password);

        const { data, error } = await supabase
          .from('viis_users')
          .insert({
            email: formData.email,
            password_hash: passwordHash,
            name: `${formData.firstName} ${formData.lastName}`.trim()
          })
          .select()
          .single();

        if (error) {
          if (error.code === '23505') {
            throw new Error('Email already exists');
          }
          throw error;
        }

        onLogin({
          uid: data.id,
          email: data.email,
          displayName: data.name
        });
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const authStyles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0f172a 100%)',
      color: 'white',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    },
    card: {
      background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6), rgba(59, 130, 246, 0.1))',
      borderRadius: '1.5rem',
      padding: '3rem',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      backdropFilter: 'blur(20px)',
      width: '100%',
      maxWidth: '420px',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
    },
    header: {
      textAlign: 'center',
      marginBottom: '2.5rem'
    },
    logo: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      background: 'linear-gradient(135deg, #60a5fa, #a855f7)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent',
      marginBottom: '0.5rem'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.875rem 1rem',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      border: '2px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '0.75rem',
      color: 'white',
      outline: 'none',
      fontSize: '0.875rem'
    },
    button: {
      padding: '0.875rem 1.5rem',
      borderRadius: '0.75rem',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem'
    },
    primaryButton: {
      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      color: 'white'
    },
    googleButton: {
      background: 'linear-gradient(135deg, #ffffff, #f3f4f6)',
      color: '#1f2937',
      border: '1px solid rgba(0, 0, 0, 0.1)'
    }
  };

  return (
    <div style={authStyles.container}>
      <div style={authStyles.card}>
        <div style={authStyles.header}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <Shield size={48} color="#60a5fa" />
            <h1 style={authStyles.logo}>VIIS</h1>
          </div>
          <p style={{ fontSize: '0.875rem', color: '#93c5fd', marginBottom: '2rem' }}>
            Video Intelligence & Identification System
          </p>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0, color: '#f8fafc' }}>
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.75rem',
            color: '#fca5a5',
            fontSize: '0.875rem',
            marginBottom: '1rem'
          }}>
            <AlertTriangle size={16} style={{ marginRight: '0.5rem', display: 'inline' }} />
            {error}
          </div>
        )}

        <form onSubmit={handleEmailPasswordAuth} style={authStyles.form}>
          {!isLogin && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={authStyles.inputGroup}>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  style={authStyles.input}
                  placeholder="John"
                  required
                />
              </div>
              <div style={authStyles.inputGroup}>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  style={authStyles.input}
                  placeholder="Doe"
                  required
                />
              </div>
            </div>
          )}

          <div style={authStyles.inputGroup}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              style={authStyles.input}
              placeholder="john.doe@example.com"
              required
            />
          </div>

          <div style={authStyles.inputGroup}>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              style={authStyles.input}
              placeholder="••••••••"
              required
            />
          </div>

          {!isLogin && (
            <div style={authStyles.inputGroup}>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                style={authStyles.input}
                placeholder="••••••••"
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...authStyles.button,
              ...authStyles.primaryButton,
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '50%',
                  borderTopColor: 'white',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Please wait...
              </>
            ) : (
              <>
                {isLogin ? <Users size={18} /> : <UserPlus size={18} />}
                {isLogin ? 'Sign In' : 'Create Account'}
              </>
            )}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '2rem', fontSize: '0.875rem', color: '#94a3b8' }}>
          <span>{isLogin ? "Don't have an account?" : "Already have an account?"}</span>
          <button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            style={{
              background: 'none',
              color: '#60a5fa',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '0.875rem',
              marginLeft: '0.5rem'
            }}
          >
            {isLogin ? 'Sign Up' : 'Sign In'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ==================== AUTHENTICATION WRAPPER ====================

const AuthWrapper = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem('viis_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setIsLoading(false);
  }, []);

  const handleLogin = (user) => {
    localStorage.setItem('viis_user', JSON.stringify(user));
    setUser(user);
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem('viis_user');
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0f172a 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Shield size={64} color="#60a5fa" />
          <h2 style={{ margin: '1rem 0', fontSize: '2rem' }}>VIIS</h2>
          <p style={{ color: '#94a3b8' }}>Initializing security system...</p>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(96, 165, 250, 0.3)',
            borderTop: '4px solid #60a5fa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '2rem auto'
          }}></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthPage onLogin={handleLogin} />;
  }

  return <VIISApp user={user} onLogout={handleLogout} />;
};

// Add this function right after the SECURITY_PROMPT constant and before const VIISApp = ({ user, onLogout }) => {

  const formatAnalysisReport = (rawResponse) => {
    if (!rawResponse) return 'No analysis data available';
    
    return rawResponse
      // Replace **TEXT:** with styled headers
      .replace(/\*\*([^*]+):\*\*/g, '<strong style="color: #60a5fa; font-weight: 700;">$1:</strong>')
      // Replace **TEXT** with bold text
      .replace(/\*\*([^*]+)\*\*/g, '<strong style="color: #f8fafc; font-weight: 600;">$1</strong>')
      // Replace * bullet points with styled bullets
      .replace(/^\* /gm, '• ')
      // Clean up any remaining asterisks
      .replace(/\*/g, '');
  };

// ==================== MAIN VIIS APP ====================

const VIISApp = ({ user, onLogout }) => {
  // Core State
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [notifications, setNotifications] = useState([]);
  
  // UI State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [streamConnected, setStreamConnected] = useState(false);
  
  // Modal State
  const [showAddCamera, setShowAddCamera] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [playingRecording, setPlayingRecording] = useState(null);

  // YouTube State
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isDownloadingYoutube, setIsDownloadingYoutube] = useState(false);

  // Analysis Mode: 'security' | 'traffic'
  const [analysisMode, setAnalysisMode] = useState('security');

  // Form State
  const [newCamera, setNewCamera] = useState({
    name: '', rtspUrl: '', location: '', customPrompt: '',
    emailNotifications: '', smsNotifications: '', serial: '', sitename: ''
  });
  
  // Refs
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunks = useRef([]);
  const hlsInstance = useRef(null);

  // ==================== UTILITY FUNCTIONS ====================
  
  const addNotification = (type, message) => {
    const notification = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type, message, timestamp: new Date()
    };
    setNotifications(prev => [...prev, notification]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  const videoToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // ==================== SUPABASE FUNCTIONS ====================

  const loadCameras = async () => {
    try {
      // Initial load
      const { data, error } = await supabase
        .from('viis_cameras')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const camerasData = data.map(item => ({
        id: item.id,
        name: item.name,
        rtspUrl: item.rtsp_url,
        location: item.location,
        customPrompt: item.custom_prompt,
        emailNotifications: item.email_notifications,
        smsNotifications: item.sms_notifications,
        serial: item.serial,
        sitename: item.sitename,
        status: item.status,
        resolution: item.resolution,
        fps: item.fps,
        addedAt: new Date(item.created_at)
      }));

      setCameras(camerasData);
      setIsLoading(false);

      if (camerasData.length > 0 && !selectedCamera) {
        setSelectedCamera(camerasData[0]);
      }

      // Subscribe to realtime updates
      const channel = supabase
        .channel('cameras-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'viis_cameras' }, async () => {
          const { data: newData } = await supabase
            .from('viis_cameras')
            .select('*')
            .order('created_at', { ascending: false });

          const formatted = newData.map(item => ({
            id: item.id,
            name: item.name,
            rtspUrl: item.rtsp_url,
            location: item.location,
            customPrompt: item.custom_prompt,
            emailNotifications: item.email_notifications,
            smsNotifications: item.sms_notifications,
            serial: item.serial,
            sitename: item.sitename,
            status: item.status,
            resolution: item.resolution,
            fps: item.fps,
            addedAt: new Date(item.created_at)
          }));
          setCameras(formatted);
        })
        .subscribe();

      return () => supabase.removeChannel(channel);
    } catch (error) {
      console.error('Error loading cameras:', error);
      addNotification('error', 'Failed to load cameras');
      setIsLoading(false);
    }
  };

  const loadRecordings = async () => {
    try {
      const { data, error } = await supabase
        .from('viis_recordings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const recordingsData = data.map(item => ({
        id: item.id,
        name: item.name,
        camera: item.camera,
        downloadURL: item.download_url,
        fileName: item.file_name,
        size: item.size,
        duration: item.duration,
        timestamp: new Date(item.created_at)
      }));

      setRecordings(recordingsData);

      // Subscribe to realtime updates
      const channel = supabase
        .channel('recordings-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'viis_recordings' }, async () => {
          const { data: newData } = await supabase
            .from('viis_recordings')
            .select('*')
            .order('created_at', { ascending: false });

          const formatted = newData.map(item => ({
            id: item.id,
            name: item.name,
            camera: item.camera,
            downloadURL: item.download_url,
            fileName: item.file_name,
            size: item.size,
            duration: item.duration,
            timestamp: new Date(item.created_at)
          }));
          setRecordings(formatted);
        })
        .subscribe();

      return () => supabase.removeChannel(channel);
    } catch (error) {
      console.error('Error loading recordings:', error);
      addNotification('error', 'Failed to load recordings');
    }
  };

  const loadAnalysisResults = async () => {
    try {
      const { data, error } = await supabase
        .from('viis_analysis')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const analysisData = data.map(item => ({
        id: item.id,
        cameraName: item.camera_name,
        threatLevel: item.threat_level,
        incidentType: item.incident_type,
        description: item.description,
        rawResponse: item.raw_response,
        videoUrl: item.video_url,
        recordingId: item.recording_id,
        analysisType: item.analysis_type || 'security',
        classification: item.analysis_type === 'traffic'
          ? (item.threat_level === 'HIGH' ? 'MAJOR' : item.threat_level === 'MEDIUM' ? 'MINOR' : 'TRAFFIC_REPORT')
          : undefined,
        timestamp: new Date(item.created_at)
      }));

      setAnalysisResults(analysisData);

      // Subscribe to realtime updates
      const channel = supabase
        .channel('analysis-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'viis_analysis' }, async () => {
          const { data: newData } = await supabase
            .from('viis_analysis')
            .select('*')
            .order('created_at', { ascending: false });

          const formatted = newData.map(item => ({
            id: item.id,
            cameraName: item.camera_name,
            threatLevel: item.threat_level,
            incidentType: item.incident_type,
            description: item.description,
            rawResponse: item.raw_response,
            videoUrl: item.video_url,
            analysisType: item.analysis_type || 'security',
            classification: item.analysis_type === 'traffic'
              ? (item.threat_level === 'HIGH' ? 'MAJOR' : item.threat_level === 'MEDIUM' ? 'MINOR' : 'TRAFFIC_REPORT')
              : undefined,
            timestamp: new Date(item.created_at)
          }));
          setAnalysisResults(formatted);
        })
        .subscribe();

      return () => supabase.removeChannel(channel);
    } catch (error) {
      console.error('Error loading analysis results:', error);
      addNotification('error', 'Failed to load analysis results');
    }
  };

  const saveRecording = async (blob, recordingName, cameraName) => {
    try {
      addNotification('info', 'Uploading recording...');

      const cleanName = recordingName.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = `${Date.now()}_${cleanName}.webm`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('recordings')
        .upload(fileName, blob, {
          contentType: 'video/webm',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('recordings')
        .getPublicUrl(fileName);

      const downloadURL = urlData.publicUrl;

      // Save to database and get the recording ID
      const { data: recordingData, error: dbError } = await supabase
        .from('viis_recordings')
        .insert({
          name: recordingName,
          camera: cameraName,
          download_url: downloadURL,
          file_name: `recordings/${fileName}`,
          size: Math.round(blob.size / 1024 / 1024 * 100) / 100 + ' MB',
          duration: '00:15'
        })
        .select('id')
        .single();

      if (dbError) throw dbError;

      addNotification('success', 'Recording saved successfully');

      // Return recording info for analysis
      return {
        id: recordingData?.id,
        videoUrl: downloadURL,
        blob: blob
      };
    } catch (error) {
      console.error('Error saving recording:', error);
      addNotification('error', 'Failed to save recording');
      return null;
    }
  };

  const saveAnalysis = async (analysisData) => {
    try {
      const { error } = await supabase
        .from('viis_analysis')
        .insert({
          camera_name: analysisData.cameraName,
          threat_level: analysisData.threatLevel,
          incident_type: analysisData.incidentType,
          description: analysisData.description,
          raw_response: analysisData.rawResponse,
          video_url: analysisData.videoUrl || null,
          recording_id: analysisData.recordingId || null,
          analysis_type: analysisData.analysisType || 'security',
          timestamp: new Date()
        });

      if (error) throw error;
      addNotification('success', 'Analysis saved successfully');
    } catch (error) {
      console.error('Error saving analysis:', error);
      addNotification('error', 'Failed to save analysis');
    }
  };

  // ==================== CAMERA FUNCTIONS ====================

  const addCamera = async () => {
    if (!newCamera.name || !newCamera.rtspUrl) {
      addNotification('error', 'Please fill in camera name and RTSP URL');
      return;
    }

    try {
      const { error } = await supabase
        .from('viis_cameras')
        .insert({
          name: newCamera.name,
          rtsp_url: newCamera.rtspUrl,
          location: newCamera.location,
          custom_prompt: newCamera.customPrompt,
          email_notifications: newCamera.emailNotifications,
          sms_notifications: newCamera.smsNotifications,
          serial: newCamera.serial,
          sitename: newCamera.sitename,
          status: 'active',
          resolution: '1080p',
          fps: 30
        });

      if (error) throw error;

      setNewCamera({
        name: '', rtspUrl: '', location: '', customPrompt: '',
        emailNotifications: '', smsNotifications: '', serial: '', sitename: ''
      });
      setShowAddCamera(false);
      addNotification('success', 'Camera added successfully');
    } catch (error) {
      console.error('Error adding camera:', error);
      addNotification('error', 'Failed to add camera');
    }
  };

  const initializeWebcam = async () => {
    try {
      addNotification('info', 'Connecting to webcam...');
      
      let retries = 0;
      while (!videoRef.current && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
  
      if (!videoRef.current) {
        addNotification('error', 'Video element not available for webcam');
        setStreamConnected(false);
        return;
      }
      
      if (hlsInstance.current) {
        hlsInstance.current.destroy();
        hlsInstance.current = null;
      }
      
      const video = videoRef.current;
      
      if (video.srcObject) {
        const tracks = video.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }
      video.src = '';
      video.srcObject = null;
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 1280, height: 720 }, 
        audio: false 
      });
      
      video.srcObject = stream;
      await video.play();
      setStreamConnected(true);
      setSelectedCamera({
        id: 'webcam',
        name: 'Local Webcam',
        location: 'Local Device',
        resolution: '720p',
        fps: '30',
        status: 'active'
      });
      addNotification('success', 'Connected to local webcam');
    } catch (error) {
      console.error('Webcam initialization failed:', error);
      addNotification('error', 'Failed to access webcam. Please check permissions.');
      setStreamConnected(false);
    }
  };

  const initializeStream = async (camera) => {
    try {
      addNotification('info', `Connecting to ${camera.name} stream...`);
      
      if (!camera.rtspUrl) {
        addNotification('error', `No RTSP URL configured for ${camera.name}`);
        setStreamConnected(false);
        return;
      }

      let retries = 0;
      while (!videoRef.current && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!videoRef.current) {
        addNotification('error', 'Video element not available after waiting');
        setStreamConnected(false);
        return;
      }

      const video = videoRef.current;
      
      video.pause();
      video.removeAttribute('src');
      video.load();
      
      if (video.srcObject) {
        const tracks = video.srcObject.getTracks();
        tracks.forEach(track => track.stop());
        video.srcObject = null;
      }
      
      if (hlsInstance.current) {
        hlsInstance.current.destroy();
        hlsInstance.current = null;
      }

      console.log('Connecting to camera stream:', camera.rtspUrl);
      
      if (Hls.isSupported()) {
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: true,
          maxBufferLength: 30,
          maxMaxBufferLength: 600,
          maxBufferSize: 60 * 1000 * 1000,
          maxBufferHole: 0.5,
          highBufferWatchdogPeriod: 2,
          nudgeOffset: 0.2,
          nudgeMaxRetry: 5,
          backBufferLength: 90
        });

        hls.loadSource(camera.rtspUrl);
        hls.attachMedia(video);
        
        hls.on(Hls.Events.MANIFEST_PARSED, function() {
          video.muted = false;
          video.play().catch(function(error) {
            console.log("Play failed:", error);
            video.muted = true;
            video.play().catch(function(error) {
              console.log("Muted play also failed:", error);
            });
          });
          setStreamConnected(true);
          addNotification('success', `Connected to ${camera.name} HLS stream`);
        });

        hls.on(Hls.Events.ERROR, function(event, data) {
          if (data.fatal) {
            switch(data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log('Network error, trying to recover...');
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log('Media error, trying to recover...');
                hls.recoverMediaError();
                break;
              default:
                console.log('Fatal error, destroying HLS instance...');
                hls.destroy();
                setStreamConnected(false);
                addNotification('error', `Fatal error connecting to ${camera.name}`);
                break;
            }
          }
        });

        hlsInstance.current = hls;
        
      } else {
        video.src = camera.rtspUrl;
        video.addEventListener('loadedmetadata', function() {
          video.play().catch(function(error) {
            console.log("Safari play failed:", error);
          });
          setStreamConnected(true);
          addNotification('success', `Connected to ${camera.name} stream (Safari native)`);
        });
        
        video.addEventListener('error', function(error) {
          console.error('Video error:', error);
          setStreamConnected(false);
          addNotification('error', `Failed to connect to ${camera.name}. Check RTSP URL: ${camera.rtspUrl}`);
        });
      }
      
    } catch (error) {
      console.error('Stream initialization failed:', error);
      addNotification('error', `Failed to initialize ${camera.name} stream: ${error.message}`);
      setStreamConnected(false);
    }
  };

  // ==================== RECORDING FUNCTIONS ====================

  const startRecording = async () => {
    try {
      setIsRecording(true);
      addNotification('info', 'Starting recording...');
      
      let stream;
      const video = videoRef.current;

      if (selectedCamera?.id === 'webcam') {
        stream = video.srcObject || await navigator.mediaDevices.getUserMedia({ 
          video: { width: 1280, height: 720 }, audio: true 
        });
      } else if (video.captureStream) {
        stream = video.captureStream(30);
      } else {
        throw new Error('Cannot capture stream from this source');
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 2500000
      });
      
      mediaRecorderRef.current = mediaRecorder;
      recordedChunks.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunks.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (recordedChunks.current.length === 0) {
          addNotification('error', 'No recording data captured');
          return;
        }

        const blob = new Blob(recordedChunks.current, { type: 'video/webm' });
        const recordingName = `Recording_${selectedCamera?.name || 'Camera'}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}`;

        const recordingInfo = await saveRecording(blob, recordingName, selectedCamera?.name || 'Unknown');

        // Trigger analysis with recording info
        if (recordingInfo) {
          analyzeVideo(blob, selectedCamera?.customPrompt || '', recordingName, recordingInfo.videoUrl, recordingInfo.id);
        }
      };

      mediaRecorder.start(1000);
      addNotification('success', 'Recording started');
      
    } catch (error) {
      console.error('Recording error:', error);
      addNotification('error', 'Failed to start recording');
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      addNotification('info', 'Recording stopped');
    }
  };

  const deleteRecording = async (recording) => {
    try {
      // Delete from storage
      if (recording.file_name) {
        const fileName = recording.file_name.replace('recordings/', '');
        await supabase.storage.from('recordings').remove([fileName]);
      }

      // Delete from database
      if (recording.id) {
        const { error } = await supabase
          .from('viis_recordings')
          .delete()
          .eq('id', recording.id);

        if (error) throw error;
      }

      addNotification('success', 'Recording deleted');
    } catch (error) {
      console.error('Delete error:', error);
      addNotification('error', 'Failed to delete recording');
    }
  };

  const downloadRecording = (recording) => {
    try {
      const url = recording.downloadURL || URL.createObjectURL(recording.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = recording.name || 'recording.webm';
      a.click();
      addNotification('success', 'Download started');
    } catch (error) {
      console.error('Download error:', error);
      addNotification('error', 'Failed to download');
    }
  };

  // ==================== ANALYSIS FUNCTIONS ====================

  const captureLiveFrame = async () => {
    return new Promise((resolve, reject) => {
      try {
        const video = videoRef.current;
        if (!video) {
          reject(new Error('No video element'));
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], 'live-capture.png', { type: 'image/png' }));
          } else {
            reject(new Error('Failed to capture frame'));
          }
        }, 'image/png');

      } catch (error) {
        reject(error);
      }
    });
  };

  // Extract a frame from a video File/Blob for AI analysis
  const captureFrameFromVideo = async (videoInput) => {
    return new Promise((resolve, reject) => {
      let settled = false;
      const done = (err, result) => {
        if (settled) return;
        settled = true;
        try {
          video.remove();
          if (url) URL.revokeObjectURL(url);
        } catch (e) {}
        if (err) reject(err);
        else resolve(result);
      };

      let url = null;
      let video = null;

      try {
        video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.crossOrigin = 'anonymous';
        video.preload = 'auto';
        // Must be in DOM for some browsers to decode
        video.style.position = 'fixed';
        video.style.top = '-9999px';
        video.style.left = '-9999px';
        video.style.width = '1px';
        video.style.height = '1px';
        video.style.opacity = '0';
        video.style.pointerEvents = 'none';
        document.body.appendChild(video);

        url = videoInput instanceof File || videoInput instanceof Blob
          ? URL.createObjectURL(videoInput)
          : videoInput;

        let frameCaptured = false;

        const captureFrame = () => {
          if (frameCaptured) return;
          frameCaptured = true;

          const w = video.videoWidth || 1280;
          const h = video.videoHeight || 720;

          if (w === 0 || h === 0) {
            done(new Error('Video has no usable resolution'));
            return;
          }

          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, w, h);

          canvas.toBlob((blob) => {
            if (blob) {
              done(null, new File([blob], 'video-frame.png', { type: 'image/png' }));
            } else {
              done(new Error('Failed to capture frame from video'));
            }
          }, 'image/png');
        };

        video.onloadeddata = () => {
          // Seek a few seconds in for a representative frame
          const seekTime = video.duration > 4 ? 2 : (video.duration > 0 ? video.duration * 0.3 : 0);
          if (seekTime > 0 && video.duration > 0.5) {
            video.currentTime = seekTime;
          } else {
            // Video too short — capture immediately
            captureFrame();
          }
        };

        video.onseeked = () => captureFrame();
        video.onerror = () => done(new Error('Failed to load video for frame extraction'));

        // Fallback timeout: if seeking doesn't fire within 5s, capture anyway
        const fallbackTimer = setTimeout(() => {
          if (!settled && video.readyState >= 2) {
            captureFrame();
          }
        }, 5000);

        // Hard timeout
        const hardTimer = setTimeout(() => {
          done(new Error('Video load timed out'));
        }, 20000);

        // Clear fallback on success
        const origDone = done;
        done = (err, result) => {
          clearTimeout(fallbackTimer);
          clearTimeout(hardTimer);
          origDone(err, result);
        };

        video.src = url;
        video.load();

      } catch (error) {
        done(error);
      }
    });
  };

  // Enhanced parsing function with improved threat classification
  const parseGeminiResponseSimple = (analysisText) => {
    try {
      // Check for "THREAT LEVEL: NONE" - no incidents detected
      if (analysisText.includes('THREAT LEVEL: NONE') || analysisText.trim() === 'THREAT LEVEL: NONE') {
        return {
          threatLevel: 'NONE',
          incidentType: 'No Security Incidents',
          rawResponse: 'THREAT LEVEL: NONE\n\nNo security incidents or threats detected in the surveillance footage.',
          description: 'AI analysis shows no security incidents or threats detected',
          subjects: { 
            count: 0, 
            descriptions: 'No suspicious subjects or activities identified' 
          },
          recommendedAction: 'Continue routine monitoring procedures',
          evidence: 'Video analysis confirms normal operations'
        };
      }

      // Enhanced text parsing with improved threat classification
      const threatMatch = analysisText.match(/\*\*THREAT\s+LEVEL:\*\*\s+(EXTREME|HIGH|MEDIUM|LOW|NONE)/i) ||
                         analysisText.match(/THREAT\s+LEVEL:\s*\*?\*?(EXTREME|HIGH|MEDIUM|LOW|NONE)\*?\*?/i);
      
      let threatLevel = 'LOW';
      if (threatMatch) {
        threatLevel = threatMatch[1].toUpperCase();
        if (threatLevel === 'NONE') {
          return {
            threatLevel: 'NONE',
            incidentType: 'No Security Incidents',
            rawResponse: analysisText,
            description: 'No security incidents or threats detected',
            subjects: { count: 0, descriptions: 'No suspicious activity identified' },
            recommendedAction: 'Continue routine monitoring procedures',
            evidence: 'Video analysis confirms normal operations'
          };
        }
      }

      // Enhanced incident type extraction with length limits
      const incidentMatch = analysisText.match(/\*\*INCIDENT\s+TYPE:\*\*\s+([^\*\n\r]+?)(?=\s*\*\*|$)/i) ||
                           analysisText.match(/INCIDENT\s+TYPE:\s*([^\n\r]+?)(?=\s*\*\*|$)/i);
      
      let incidentType = 'Security Event';
      if (incidentMatch) {
        // Limit incident type to 4 words max
        const words = incidentMatch[1].trim().split(' ');
        incidentType = words.slice(0, 4).join(' ');
      }

      // Improved threat level classification based on content keywords
      const analysisLower = analysisText.toLowerCase();
      
      // EXTREME threat keywords
      if (analysisLower.includes('gun') || analysisLower.includes('firearm') || analysisLower.includes('weapon') || 
          analysisLower.includes('armed') || analysisLower.includes('shooting') || analysisLower.includes('violence') || 
          analysisLower.includes('assault') || analysisLower.includes('explosive') || analysisLower.includes('bomb') ||
          analysisLower.includes('kidnapping') || analysisLower.includes('hostage')) {
        threatLevel = 'EXTREME';
      } 
      // HIGH threat keywords
      else if (analysisLower.includes('theft') || analysisLower.includes('stealing') || analysisLower.includes('robbery') || 
               analysisLower.includes('robbing') || analysisLower.includes('burglary') || analysisLower.includes('hijacking') || 
               analysisLower.includes('carjacking') || analysisLower.includes('breaking') || analysisLower.includes('vandalism') || 
               analysisLower.includes('trespassing') || analysisLower.includes('break-in') || analysisLower.includes('intrusion') ||
               analysisLower.includes('drug') || analysisLower.includes('criminal')) {
        threatLevel = 'HIGH';
      } 
      // MEDIUM threat keywords
      else if (analysisLower.includes('suspicious') || analysisLower.includes('loitering') || 
               analysisLower.includes('unauthorized') || analysisLower.includes('unusual') ||
               analysisLower.includes('reconnaissance') || analysisLower.includes('disturbance')) {
        threatLevel = 'MEDIUM';
      }

      // Extract other data
      const countMatch = analysisText.match(/[\*\-]\s*\*?Count:\*?\s*(\d+)/i);
      let subjectCount = 0;
      if (countMatch) {
        subjectCount = parseInt(countMatch[1]);
      }

      const actionMatch = analysisText.match(/\*\*RECOMMENDED\s+ACTION:\*\*\s+((?:[^*]|\*(?!\*))+?)(?=\s*\*\*EVIDENCE|\*\*$|$)/i) ||
                         analysisText.match(/RECOMMENDED\s+ACTION:\s*((?:[^*]|\*(?!\*))+?)(?=\s*\*\*EVIDENCE|\*\*$|$)/i);
      
      let recommendedAction = 'Review analysis and take appropriate action';
      if (actionMatch) {
        recommendedAction = actionMatch[1].trim();
      }

      return {
        threatLevel,
        incidentType,
        rawResponse: analysisText,
        description: `${threatLevel} threat level detected`,
        subjects: { count: subjectCount, descriptions: 'See detailed analysis below' },
        recommendedAction,
        evidence: 'See detailed analysis below'
      };
      
    } catch (parseError) {
      console.error('Parse error:', parseError);
      
      return {
        threatLevel: 'MEDIUM',
        incidentType: 'Analysis Review Required',
        rawResponse: analysisText,
        description: 'Security analysis completed - review required',
        subjects: { count: 0, descriptions: 'Analysis completed' },
        recommendedAction: 'Review analysis results',
        evidence: 'AI analysis available'
      };
    }
  };

  // Parse traffic infringement analysis response
  const parseTrafficResponse = (analysisText) => {
    try {
      // Check for no infringements detected
      if (analysisText.includes('NO INFRINGEMENTS DETECTED') || analysisText.includes('TRAFFIC REPORT: NO INFRINGEMENTS')) {
        return {
          threatLevel: 'NONE',
          incidentType: 'No Traffic Infringements',
          rawResponse: analysisText,
          description: 'AI analysis found no traffic infringements in the footage',
          classification: 'TRAFFIC_REPORT',
          infringementsCount: 0,
          majorCount: 0,
          minorCount: 0,
          summary: 'No traffic infringements were detected.',
          recommendedAction: 'No enforcement action required',
        };
      }

      // Count infringements by Classification markers
      const majorMatches = (analysisText.match(/\*\*Classification:\*\*\s*Major/gi) || []);
      const minorMatches = (analysisText.match(/\*\*Classification:\*\*\s*Minor/gi) || []);
      const infringementMatches = (analysisText.match(/\*\*Infringement Type:\*\*/gi) || []);

      const majorCount = majorMatches.length;
      const minorCount = minorMatches.length;
      const infringementsCount = infringementMatches.length || majorCount + minorCount;

      // Extract Final Summary
      let summary = '';
      const summaryMatch = analysisText.match(/\*\*Final Summary:\*\*\s*([\s\S]*?)$/i);
      if (summaryMatch) {
        summary = summaryMatch[1].trim().substring(0, 500);
      } else {
        // Fallback: use last paragraph
        const lines = analysisText.split('\n').filter(l => l.trim());
        summary = lines.slice(-3).join(' ').substring(0, 300);
      }

      // Determine overall classification for display
      let classification = 'TRAFFIC_REPORT';
      let threatLevel = 'NONE';
      if (majorCount > 0) {
        classification = 'MAJOR';
        threatLevel = 'HIGH';
      } else if (minorCount > 0) {
        classification = 'MINOR';
        threatLevel = 'MEDIUM';
      }

      return {
        threatLevel,
        incidentType: infringementsCount > 0
          ? `${infringementsCount} Traffic Infringement${infringementsCount > 1 ? 's' : ''}`
          : 'Traffic Analysis Report',
        rawResponse: analysisText,
        description: infringementsCount > 0
          ? `Detected ${majorCount} major and ${minorCount} minor infringements`
          : 'No traffic infringements detected',
        classification,
        infringementsCount,
        majorCount,
        minorCount,
        summary: summary || 'See detailed analysis below',
        recommendedAction: majorCount > 0
          ? `URGENT: ${majorCount} major violation${majorCount > 1 ? 's' : ''} require immediate enforcement action`
          : minorCount > 0
            ? `Review ${minorCount} minor violation${minorCount > 1 ? 's' : ''} for potential enforcement`
            : 'Continue routine traffic monitoring',
      };

    } catch (parseError) {
      console.error('Traffic parse error:', parseError);
      return {
        threatLevel: 'NONE',
        incidentType: 'Traffic Analysis Report',
        rawResponse: analysisText,
        description: 'Traffic analysis completed - review results',
        classification: 'TRAFFIC_REPORT',
        infringementsCount: 0,
        majorCount: 0,
        minorCount: 0,
        summary: 'See detailed analysis below',
        recommendedAction: 'Review analysis results',
      };
    }
  };

  const analyzeVideo = async (videoInput, customPrompt = '', uploadedFileName = null, videoUrl = null, recordingId = null) => {
    try {
      setIsAnalyzing(true);
      addNotification('info', 'Starting AI analysis...');
      
      // Select prompt based on analysis mode
      const isTrafficMode = analysisMode === 'traffic';
      let analysisPrompt = isTrafficMode ? TRAFFIC_PROMPT : SECURITY_PROMPT;

      // Camera custom prompts only apply to security mode
      if (!isTrafficMode && selectedCamera?.customPrompt?.trim()) {
        analysisPrompt += `\n\nADDITIONAL REQUIREMENTS:\n${selectedCamera.customPrompt.trim()}`;
      }
      if (customPrompt?.trim()) {
        analysisPrompt += `\n\nADDITIONAL INSTRUCTIONS:\n${customPrompt.trim()}`;
      }

      let videoFile = null;
      let mimeType = 'image/png';
      let isImageAnalysis = true;
      let isUploadedVideo = false;
      let frameFile = null;

      // Extract frame(s) for AI analysis — all paths produce a PNG image
      if (videoInput === null) {
        // Live camera — capture current frame
        addNotification('info', 'Capturing frame from live stream...');
        frameFile = await captureLiveFrame();
      } else if (videoInput instanceof File) {
        // Uploaded file — extract representative frame
        addNotification('info', 'Extracting frame from video...');
        isUploadedVideo = !selectedCamera || uploadedFileName;
        frameFile = await captureFrameFromVideo(videoInput);
      } else if (videoInput instanceof Blob) {
        // Recording blob — extract frame
        addNotification('info', 'Extracting frame from recording...');
        frameFile = await captureFrameFromVideo(videoInput);
      } else if (typeof videoInput === 'string' && videoInput.startsWith('http')) {
        // URL — download then extract frame
        addNotification('info', 'Downloading video from cloud...');
        const response = await fetch(videoInput, { method: 'GET', mode: 'cors' });
        if (!response.ok) throw new Error(`Failed to download: ${response.status}`);
        const videoBlob = await response.blob();
        isUploadedVideo = true;
        addNotification('success', `Downloaded ${Math.round(videoBlob.size / 1024 / 1024 * 100) / 100} MB`);
        addNotification('info', 'Extracting frame for analysis...');
        frameFile = await captureFrameFromVideo(videoBlob);
      } else {
        throw new Error('Invalid video input');
      }

      // Convert frame to base64 (small PNG, not full video)
      addNotification('info', 'Preparing frame for AI...');
      const videoBase64 = await videoToBase64(frameFile);
      videoFile = frameFile;

      // Call Gemini 2.5 Pro via OpenRouter
      addNotification('info', 'Analyzing with AI...');

      const openRouterResponse = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': 'https://viis.novosense.africa',
          'X-Title': 'VIIS Security System',
        },
        body: JSON.stringify({
          model: OPENROUTER_MODEL,
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: analysisPrompt,
                },
                {
                  type: 'image_url',
                  image_url: {
                    url: `data:${mimeType};base64,${videoBase64}`,
                  },
                },
              ],
            },
          ],
          temperature: 0.1,
          max_tokens: 8000,
        }),
      });

      if (!openRouterResponse.ok) {
        const errBody = await openRouterResponse.text();
        let errMsg = `AI service error: ${openRouterResponse.status}`;
        try {
          const errJson = JSON.parse(errBody);
          errMsg = errJson.error?.message || errMsg;
        } catch (e) {}
        throw new Error(errMsg);
      }

      const orResult = await openRouterResponse.json();

      // Gemini 2.5 Pro is a thinking model — content may be null if reasoning consumed all tokens
      let analysisText = orResult.choices?.[0]?.message?.content;
      if (!analysisText) {
        // Fallback: check if reasoning is available and retry with higher token limit
        const reasoning = orResult.choices?.[0]?.message?.reasoning;
        const finishReason = orResult.choices?.[0]?.finish_reason;
        if (finishReason === 'length' && reasoning) {
          throw new Error(
            'AI response was cut off — video may be too long or prompt too complex. Try a shorter video clip.'
          );
        }
        throw new Error('No response received from AI model. The service may be temporarily unavailable.');
      }
      console.log('Raw AI response:', analysisText);
      
      // Use the enhanced parsing function
      // Use appropriate parser based on analysis mode
      const analysis = isTrafficMode
        ? parseTrafficResponse(analysisText)
        : parseGeminiResponseSimple(analysisText);

      // Add metadata
      analysis.location = isUploadedVideo ? 'Uploaded Video' : (selectedCamera?.location || 'Unknown');
      analysis.cameraId = isUploadedVideo ? null : (selectedCamera?.id || null);
      analysis.cameraName = isUploadedVideo ? (uploadedFileName || 'Uploaded Video') : (selectedCamera?.name || 'Unknown');
      analysis.analysisMethod = isTrafficMode
        ? 'AI Traffic Analysis - Uploaded Video'
        : (isUploadedVideo ? 'AI Analysis - Uploaded Video' : 'AI Analysis - Live Camera');
      analysis.videoSize = Math.round(videoFile.size / 1024 * 100) / 100 + ' KB'; // Frame PNG size
      analysis.analysisType = isTrafficMode ? 'traffic' : 'security';
      analysis.analysisDisplayType = 'Frame Analysis (PNG)';

      // Store video reference for playback (only for uploaded videos, not live captures)
      if (isUploadedVideo) {
        if (videoUrl) {
          analysis.videoUrl = videoUrl;
        } else if (typeof videoInput === 'string' && videoInput.startsWith('http')) {
          analysis.videoUrl = videoInput;
        } else if (videoInput instanceof File) {
          analysis.videoFileName = uploadedFileName || videoInput.name;
        }
      }

      // Store recording ID for linking to video
      if (recordingId) {
        analysis.recordingId = recordingId;
      }

      console.log('Final analysis object:', analysis);

      // Save to Supabase
      await saveAnalysis(analysis);
      
      // Send notifications for high-priority threats only
      if (['HIGH', 'EXTREME'].includes(analysis.threatLevel) && analysis.threatLevel !== 'NONE') {
        await sendNotifications(analysis);
      }
      
      addNotification('success', `Analysis complete - ${analysis.threatLevel} threat level detected`);
      setIsAnalyzing(false);
      
    } catch (error) {
      console.error('Analysis error:', error);
      addNotification('error', `Analysis failed: ${error.message}`);
      setIsAnalyzing(false);
    }
  };

  // ==================== NOTIFICATION FUNCTIONS ====================

  const sendSMSViaBulkSMS = async (phoneNumbers, message) => {
    try {
      const postData = JSON.stringify({
        'to': phoneNumbers,
        'body': message
      });

      const auth = btoa(`${BULKSMS_CONFIG.username}:${BULKSMS_CONFIG.password}`);

      const response = await fetch(BULKSMS_CONFIG.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${auth}`
        },
        body: postData
      });

      if (!response.ok) {
        throw new Error(`BulkSMS API Error: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json();
      console.log('BulkSMS Response:', result);
      
      return { success: true, data: result };
    } catch (error) {
      console.error('BulkSMS Error:', error);
      return { success: false, error: error.message };
    }
  };

  const testSMSNotification = async () => {
    if (!selectedCamera || !selectedCamera.smsNotifications) {
      addNotification('error', 'No SMS recipients configured for selected camera');
      return;
    }

    const phones = selectedCamera.smsNotifications
      .split(',')
      .map(p => p.trim())
      .filter(p => p.length > 0);

    if (phones.length === 0) {
      addNotification('error', 'No valid phone numbers found');
      return;
    }

    const testMessage = `🧪 VIIS TEST MESSAGE

This is a test SMS from the VIIS Security System.

Camera: ${selectedCamera.name}
Location: ${selectedCamera.location || 'Not specified'}
Time: ${new Date().toLocaleString()}

If you received this message, SMS notifications are working correctly.

- VIIS Team`;

    addNotification('info', `Sending test SMS to ${phones.length} recipient(s)...`);

    try {
      const result = await sendSMSViaBulkSMS(phones, testMessage);
      
      if (result.success) {
        addNotification('success', `✅ Test SMS sent successfully to ${phones.length} recipient(s)`);
        console.log('Test SMS sent to:', phones);
      } else {
        addNotification('error', `❌ Test SMS failed: ${result.error}`);
        console.error('Test SMS failed:', result.error);
      }
    } catch (error) {
      addNotification('error', `❌ Test SMS error: ${error.message}`);
      console.error('Test SMS error:', error);
    }
  };

  const sendNotifications = async (analysis) => {
    const alertMessage = `🚨 VIIS SECURITY ALERT

Threat Level: ${analysis.threatLevel}
Camera: ${analysis.cameraName || 'Unknown'}
Location: ${analysis.location || 'Unknown'}
Incident: ${analysis.incidentType}
Time: ${new Date().toLocaleString()}

Description: ${analysis.description}

Recommended Action: ${analysis.recommendedAction}

This is an automated security alert from the VIIS system.`;

    // Send SMS notifications
    if (selectedCamera?.smsNotifications) {
      const phones = selectedCamera.smsNotifications
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0);
      
      if (phones.length > 0) {
        addNotification('info', `Sending SMS alerts to ${phones.length} recipient(s)...`);
        
        const smsResult = await sendSMSViaBulkSMS(phones, alertMessage);
        
        if (smsResult.success) {
          addNotification('success', `📱 SMS alerts sent successfully to ${phones.length} recipient(s)`);
          phones.forEach(phone => {
            console.log('📱 SMS sent to:', phone);
          });
        } else {
          addNotification('error', `Failed to send SMS alerts: ${smsResult.error}`);
          console.error('SMS sending failed:', smsResult.error);
        }
      }
    }

    if (!selectedCamera?.smsNotifications && !selectedCamera?.emailNotifications) {
      addNotification('warning', 'No notification recipients configured for this camera');
    }
  };

  // ==================== FILE UPLOAD ====================

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file?.type.startsWith('video/')) {
      addNotification('error', 'Please select a video file');
      return;
    }

    try {
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = `${Date.now()}_${cleanFileName}`;

      addNotification('info', 'Uploading video...');

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('uploads')
        .upload(fileName, file, {
          contentType: file.type || 'video/mp4',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('uploads')
        .getPublicUrl(fileName);

      const downloadURL = urlData.publicUrl;

      // Save to database
      const { data: recordingData, error: dbError } = await supabase
        .from('viis_recordings')
        .insert({
          name: file.name,
          camera: 'Uploaded File',
          download_url: downloadURL,
          file_name: `uploads/${fileName}`,
          size: Math.round(file.size / 1024 / 1024 * 100) / 100 + ' MB',
          duration: 'Unknown'
        })
        .select('id')
        .single();

      if (dbError) throw dbError;

      analyzeVideo(file, '', file.name, downloadURL, recordingData?.id);
      setShowUpload(false);
      addNotification('success', 'Video uploaded and analysis started');
    } catch (error) {
      console.error('Upload error:', error);
      addNotification('error', 'Upload failed');
    }
  };

  // ==================== YOUTUBE INTEGRATION ====================

  const handleYoutubeAnalyze = async () => {
    if (!youtubeUrl.trim()) {
      addNotification('error', 'Please enter a YouTube URL');
      return;
    }

    const patterns = [
      /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]+/,
      /^https?:\/\/youtu\.be\/[\w-]+/,
      /^https?:\/\/(www\.)?youtube\.com\/shorts\/[\w-]+/,
    ];

    if (!patterns.some(p => p.test(youtubeUrl.trim()))) {
      addNotification('error', 'Invalid YouTube URL. Expected: https://www.youtube.com/watch?v=...');
      return;
    }

    try {
      setIsDownloadingYoutube(true);
      addNotification('info', 'Downloading YouTube video...');

      const response = await fetch('/api/youtube/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ youtubeUrl: youtubeUrl.trim() }),
      });

      // Check response content type — non-JSON means backend is down
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          'Backend server is not running. Please start the backend with: node server.js'
        );
      }

      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = JSON.parse(await response.text());
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // Response body wasn't JSON
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Download failed');

      const { buffer, title, mimeType, size } = result.data;
      addNotification('success', `Downloaded "${title}" (${size})`);

      // Convert base64 to Blob, then File
      const binaryStr = atob(buffer);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType || 'video/mp4' });
      const file = new File([blob], `${title || 'youtube-video'}.mp4`, { type: mimeType || 'video/mp4' });

      setIsDownloadingYoutube(false);
      setShowUpload(false);
      setYoutubeUrl('');

      // Feed into existing analysis pipeline
      analyzeVideo(file, '', title || 'YouTube Video', null, null);

    } catch (error) {
      console.error('YouTube download error:', error);
      addNotification('error', `YouTube download failed: ${error.message}`);
      setIsDownloadingYoutube(false);
    }
  };

  // ==================== EFFECTS ====================

  useEffect(() => {
    let unsubscribeCameras, unsubscribeRecordings, unsubscribeAnalysis;

    const initializeData = async () => {
      unsubscribeCameras = await loadCameras();
      unsubscribeRecordings = await loadRecordings();
      unsubscribeAnalysis = await loadAnalysisResults();
    };

    initializeData();

    return () => {
      if (hlsInstance.current) {
        hlsInstance.current.destroy();
      }
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
      if (unsubscribeCameras) unsubscribeCameras();
      if (unsubscribeRecordings) unsubscribeRecordings();
      if (unsubscribeAnalysis) unsubscribeAnalysis();
    };
  }, []);

  // ==================== STYLES ====================

  const styles = {
    app: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0f172a 100%)',
      color: 'white',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    header: {
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(59, 130, 246, 0.3)',
      position: 'sticky',
      top: 0,
      zIndex: 40
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 1.5rem'
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1rem 0'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    logoText: {
      fontSize: '1.75rem',
      fontWeight: 'bold',
      background: 'linear-gradient(135deg, #60a5fa, #a855f7)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent'
    },
    nav: {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
    },
    navContent: {
      display: 'flex',
      gap: '0.5rem',
      padding: '0 1.5rem',
      overflowX: 'auto'
    },
    navItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '1rem 1.5rem',
      borderBottom: '3px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      color: '#9ca3af',
      whiteSpace: 'nowrap'
    },
    navItemActive: {
      borderBottom: '3px solid #3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      color: '#60a5fa'
    },
    main: {
      padding: '2rem 1.5rem',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    card: {
      background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(59, 130, 246, 0.05))',
      borderRadius: '1rem',
      padding: '2rem',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      backdropFilter: 'blur(10px)'
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '0.75rem',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.3s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem'
    },
    buttonPrimary: {
      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      color: 'white'
    },
    buttonDanger: {
      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
      color: 'white'
    },
    buttonSuccess: {
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white'
    },
    buttonWarning: {
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      color: 'white'
    },
    video: {
      width: '100%',
      height: '20rem',
      objectFit: 'cover',
      backgroundColor: '#000',
      borderRadius: '0.75rem',
      border: '2px solid rgba(59, 130, 246, 0.3)'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '1.5rem'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50
    },
    modalContent: {
      background: 'linear-gradient(135deg, #1e293b, #334155)',
      borderRadius: '1rem',
      padding: '2rem',
      width: '100%',
      maxWidth: '32rem',
      margin: '0 1rem',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      border: '2px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '0.75rem',
      color: 'white',
      outline: 'none'
    },
    notifications: {
      position: 'fixed',
      bottom: '1rem',
      right: '1rem',
      zIndex: 60,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      maxWidth: '400px'
    },
    notification: {
      padding: '1rem 1.5rem',
      borderRadius: '0.75rem',
      color: 'white',
      fontSize: '0.875rem',
      backdropFilter: 'blur(20px)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    loadingSpinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '50%',
      borderTopColor: '#3b82f6',
      animation: 'spin 1s linear infinite'
    }
  };

  // Enhanced threat color function
  const getThreatColor = (level) => {
    switch(level) {
      // Security threat levels
      case 'EXTREME': return '#7c2d12'; // Dark red
      case 'HIGH': return '#dc2626'; // Red
      case 'MEDIUM': return '#f59e0b'; // Orange
      case 'LOW': return '#3b82f6'; // Blue
      case 'NONE': return '#22c55e'; // Green
      // Traffic classification levels
      case 'MAJOR': return '#dc2626'; // Red - serious infringement
      case 'MINOR': return '#f59e0b'; // Orange - minor infringement
      case 'TRAFFIC_REPORT': return '#3b82f6'; // Blue - clean report
      default: return '#10b981'; // Default green
    }
  };

  if (isLoading) {
    return (
      <div style={{ ...styles.app, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Shield size={64} color="#60a5fa" />
          <h2 style={{ margin: '1rem 0', fontSize: '2rem' }}>VIIS</h2>
          <p style={{ color: '#94a3b8' }}>Loading security system...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.app}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.container}>
          <div style={styles.headerContent}>
            <div style={styles.logo}>
              <Shield size={48} color="#60a5fa" />
              <div>
                <h1 style={styles.logoText}>VIIS</h1>
                <p style={{ fontSize: '0.875rem', color: '#93c5fd', margin: 0 }}>
                  Video Intelligence & Identification System
                </p>
              </div>
            </div>

            {/* Mode Toggle: Security / Traffic */}
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '2rem',
              padding: '0.3rem',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}>
              <button
                onClick={() => setAnalysisMode('security')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '2rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  background: analysisMode === 'security'
                    ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
                    : 'transparent',
                  color: analysisMode === 'security' ? 'white' : '#94a3b8',
                }}
              >
                <Shield size={14} />
                Security
              </button>
              <button
                onClick={() => setAnalysisMode('traffic')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.5rem 1rem',
                  borderRadius: '2rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  background: analysisMode === 'traffic'
                    ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                    : 'transparent',
                  color: analysisMode === 'traffic' ? 'white' : '#94a3b8',
                }}
              >
                <Car size={14} />
                Traffic
              </button>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {streamConnected ? <Wifi size={16} color="#22c55e" /> : <WifiOff size={16} color="#ef4444" />}
                  <span style={{ fontSize: '0.75rem', color: streamConnected ? '#22c55e' : '#ef4444' }}>
                    {streamConnected ? 'CONNECTED' : 'NO STREAM'}
                  </span>
                </div>
                <div style={{ height: '20px', width: '1px', backgroundColor: 'rgba(148, 163, 184, 0.5)' }}></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '0.75rem', color: '#d1d5db', margin: 0, fontWeight: '600' }}>
                      {user?.displayName || user?.email?.split('@')[0] || 'User'}
                    </p>
                    <p style={{ fontSize: '0.65rem', color: '#94a3b8', margin: 0 }}>
                      {user?.email}
                    </p>
                  </div>
                  <button
                    onClick={onLogout}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#fca5a5',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                    title="Logout"
                  >
                    <Users size={14} />
                    Sign Out
                  </button>
                </div>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#d1d5db', margin: 0 }}>
                {analysisMode === 'traffic' ? 'Traffic Enforcement v1.0' : 'Enhanced Security v3.0'}
              </p>
              <p style={{ fontSize: '0.75rem', color: '#60a5fa', margin: 0 }}>
                {new Date().toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.container}>
          <div className="nav-tabs" style={styles.navContent}>
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Eye },
              { id: 'cameras', label: 'Cameras', icon: Camera },
              { id: 'recordings', label: 'Recordings', icon: Video },
              { id: 'analysis', label: 'Analysis', icon: AlertTriangle },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <div
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="nav-tab"
                  style={{
                    ...styles.navItem,
                    ...(activeTab === tab.id ? styles.navItemActive : {})
                  }}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.main}>
        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
              {(analysisMode === 'traffic' ? [
                { label: 'Active Cameras', value: cameras.filter(c => c.status === 'active').length, icon: Camera, color: '#60a5fa' },
                { label: 'Total Recordings', value: recordings.length, icon: Video, color: '#10b981' },
                { label: 'Traffic Reports', value: analysisResults.filter(a => a.analysisType === 'traffic').length, icon: AlertTriangle, color: '#f59e0b' },
                { label: 'Major Infringements', value: analysisResults.filter(a => a.analysisType === 'traffic' && (a.threatLevel === 'HIGH' || a.classification === 'MAJOR')).length, icon: Car, color: '#ef4444' }
              ] : [
                { label: 'Active Cameras', value: cameras.filter(c => c.status === 'active').length, icon: Camera, color: '#60a5fa' },
                { label: 'Total Recordings', value: recordings.length, icon: Video, color: '#10b981' },
                { label: 'Analysis Results', value: analysisResults.filter(a => a.analysisType !== 'traffic').length, icon: AlertTriangle, color: '#f59e0b' },
                { label: 'High Priority', value: analysisResults.filter(a => a.analysisType !== 'traffic' && ['HIGH', 'EXTREME'].includes(a.threatLevel)).length, icon: Shield, color: '#ef4444' }
              ]).map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} style={{
                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(59, 130, 246, 0.1))',
                    borderRadius: '1rem',
                    padding: '2rem',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>{stat.label}</p>
                      <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: stat.color, margin: '0.5rem 0', lineHeight: 1 }}>
                        {stat.value}
                      </p>
                    </div>
                    <Icon size={56} color={stat.color} style={{ opacity: 0.8 }} />
                  </div>
                );
              })}
            </div>

            {/* Live Feed */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '2rem' }}>
              <div style={styles.card}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#f8fafc' }}>
                  <Eye size={28} color="#60a5fa" />
                  Live Camera Feed
                  {isRecording && <div style={styles.loadingSpinner}></div>}
                </h3>
                
                <div style={{ backgroundColor: '#000', borderRadius: '0.75rem', overflow: 'hidden' }}>
                  <video ref={videoRef} style={styles.video} controls muted playsInline />
                  
                  <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(59, 130, 246, 0.1))' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <div>
                        <p style={{ fontSize: '1rem', color: '#f8fafc', margin: 0, fontWeight: '600' }}>
                          {selectedCamera ? selectedCamera.name : 'No camera selected'}
                        </p>
                        <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>
                          {selectedCamera ? selectedCamera.location || 'Location unknown' : 'Select a camera to view live feed'}
                        </p>
                      </div>
                      {streamConnected && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#22c55e' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', animation: 'pulse 2s infinite' }}></div>
                          <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>LIVE</span>
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={!selectedCamera}
                        style={{
                          ...styles.button,
                          ...(isRecording ? styles.buttonDanger : styles.buttonPrimary),
                          opacity: !selectedCamera ? 0.5 : 1
                        }}
                      >
                        {isRecording ? <StopCircle size={18} /> : <PlayCircle size={18} />}
                        {isRecording ? 'Stop Recording' : 'Start Recording'}
                      </button>
                      
                      <button onClick={initializeWebcam} style={{ ...styles.button, ...styles.buttonSuccess }}>
                        <Camera size={18} />
                        Use Webcam
                      </button>
                      
                      {selectedCamera && selectedCamera.id !== 'webcam' && (
                        <button
                          onClick={() => analyzeVideo(null, selectedCamera.customPrompt)}
                          disabled={isAnalyzing}
                          style={{ ...styles.button, ...styles.buttonWarning, opacity: isAnalyzing ? 0.5 : 1 }}
                        >
                          {isAnalyzing ? <div style={styles.loadingSpinner}></div> : <AlertTriangle size={18} />}
                          {isAnalyzing ? 'Analyzing...' : 'Analyze Live'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Analysis */}
              <div style={styles.card}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#f8fafc' }}>
                  {analysisMode === 'traffic' ? (
                    <><Car size={28} color="#f59e0b" /> Recent Traffic Reports</>
                  ) : (
                    <><AlertTriangle size={28} color="#f59e0b" /> Recent Analysis</>
                  )}
                </h3>

                <div style={{ maxHeight: '20rem', overflowY: 'auto' }}>
                  {analysisResults.filter(r =>
                    analysisMode === 'traffic' ? r.analysisType === 'traffic' : r.analysisType !== 'traffic'
                  ).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                      <AlertCircle size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No {analysisMode === 'traffic' ? 'traffic reports' : 'analysis results'} yet</p>
                      <p style={{ fontSize: '0.875rem' }}>Upload a video or analyze live feed to begin</p>
                    </div>
                  ) : (
                    analysisResults.filter(r =>
                      analysisMode === 'traffic' ? r.analysisType === 'traffic' : r.analysisType !== 'traffic'
                    ).slice(0, 5).map(result => (
                      <div
                        key={result.id}
                        onClick={() => {
                          setSelectedAnalysis(result);
                          setActiveTab('analysis');
                        }}
                        style={{
                          padding: '1.5rem',
                          borderRadius: '0.75rem',
                          borderLeft: `4px solid ${getThreatColor(result.analysisType === 'traffic' ? (result.classification || 'TRAFFIC_REPORT') : result.threatLevel)}`,
                          backgroundColor: `${getThreatColor(result.analysisType === 'traffic' ? (result.classification || 'TRAFFIC_REPORT') : result.threatLevel)}15`,
                          marginBottom: '1rem',
                          border: `1px solid ${getThreatColor(result.analysisType === 'traffic' ? (result.classification || 'TRAFFIC_REPORT') : result.threatLevel)}30`,
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateX(4px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateX(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                          <span style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '2rem',
                            fontSize: '0.75rem',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            backgroundColor: getThreatColor(result.analysisType === 'traffic' ? (result.classification || 'TRAFFIC_REPORT') : result.threatLevel),
                            color: 'white'
                          }}>
                            {result.analysisType === 'traffic' ? (result.classification || 'REPORT') : `${result.threatLevel}${result.threatLevel !== 'NONE' ? ' THREAT' : ''}`}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            {result.timestamp.toLocaleString()}
                          </span>
                        </div>
                        <h4 style={{ color: '#f8fafc', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                          {result.incidentType}
                        </h4>
                        <p style={{ fontSize: '0.875rem', color: '#d1d5db', lineHeight: 1.5, margin: 0 }}>
                          {result.rawResponse ?
                            result.rawResponse.split('\n').slice(0, 2).join(' ').substring(0, 120) + '...' :
                            'Analysis completed - click to view details'
                          }
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cameras */}
        {activeTab === 'cameras' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <div>
                <h2 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#f8fafc' }}>Camera Management</h2>
                <p style={{ color: '#94a3b8', margin: '0.5rem 0 0 0' }}>Manage your security cameras</p>
              </div>
              <button onClick={() => setShowAddCamera(true)} style={{ ...styles.button, ...styles.buttonPrimary }}>
                <Plus size={20} />
                Add Camera
              </button>
            </div>

            <div style={styles.grid}>
              {cameras.map(camera => (
                <div key={camera.id} style={{
                  background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(59, 130, 246, 0.05))',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0, color: '#f8fafc' }}>
                      {camera.name}
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#94a3b8', margin: '0.5rem 0' }}>
                      {camera.location || 'No location set'}
                    </p>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button
                      onClick={() => {
                        if (!camera.rtspUrl) {
                          addNotification('error', `No RTSP URL configured for ${camera.name}`);
                          return;
                        }
                        setSelectedCamera(camera);
                        initializeStream(camera);
                        setActiveTab('dashboard');
                      }}
                      style={{ ...styles.button, ...styles.buttonPrimary, flex: 1, fontSize: '0.75rem' }}
                    >
                      <Eye size={14} />
                      View Live
                    </button>
                  </div>
                </div>
              ))}
              
              {cameras.length === 0 && (
                <div style={{ 
                  gridColumn: '1 / -1', 
                  textAlign: 'center', 
                  padding: '4rem 2rem',
                  background: 'rgba(59, 130, 246, 0.05)',
                  borderRadius: '1rem',
                  border: '2px dashed rgba(59, 130, 246, 0.3)'
                }}>
                  <Camera size={64} style={{ marginBottom: '1rem', opacity: 0.5, color: '#64748b' }} />
                  <h3 style={{ color: '#f8fafc', marginBottom: '0.5rem' }}>No Cameras Added</h3>
                  <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>Add your first security camera</p>
                  <button onClick={() => setShowAddCamera(true)} style={{ ...styles.button, ...styles.buttonPrimary }}>
                    <Plus size={20} />
                    Add Your First Camera
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recordings */}
        {activeTab === 'recordings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <div>
                <h2 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#f8fafc' }}>Video Recordings</h2>
                <p style={{ color: '#94a3b8', margin: '0.5rem 0 0 0' }}>Manage recorded footage</p>
              </div>
              <button onClick={() => setShowUpload(true)} style={{ ...styles.button, ...styles.buttonSuccess }}>
                <Upload size={20} />
                Upload Video
              </button>
            </div>

            <div style={styles.grid}>
              {recordings.map(recording => (
                <div key={recording.id} style={{
                  background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(59, 130, 246, 0.05))',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid rgba(59, 130, 246, 0.3)'
                }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{
                      fontSize: '1.125rem',
                      fontWeight: '700',
                      color: '#f8fafc',
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      title: recording.name
                    }}>
                      {recording.name}
                    </h3>
                    <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                      <p style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{recording.camera} • {recording.size}</p>
                      <p style={{ margin: 0 }}>{recording.timestamp.toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setPlayingRecording(recording)}
                      style={{ ...styles.button, ...styles.buttonSuccess, flex: 1, fontSize: '0.75rem' }}
                    >
                      <PlayCircle size={14} />
                      Play
                    </button>
                    <button
                      onClick={() => analyzeVideo(recording.blob || recording.downloadURL, '', recording.name)}
                      disabled={isAnalyzing}
                      style={{ 
                        ...styles.button, 
                        ...styles.buttonWarning, 
                        flex: 1, 
                        fontSize: '0.75rem',
                        opacity: isAnalyzing ? 0.5 : 1
                      }}
                    >
                      {isAnalyzing ? <div style={styles.loadingSpinner}></div> : <AlertTriangle size={14} />}
                      Analyze
                    </button>
                    <button
                      onClick={() => downloadRecording(recording)}
                      style={{ ...styles.button, ...styles.buttonPrimary, fontSize: '0.75rem' }}
                    >
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete "${recording.name}"?`)) {
                          deleteRecording(recording);
                        }
                      }}
                      style={{ 
                        ...styles.button, 
                        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                        color: 'white',
                        fontSize: '0.75rem'
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
              
              {recordings.length === 0 && (
                <div style={{ 
                  gridColumn: '1 / -1', 
                  textAlign: 'center', 
                  padding: '4rem 2rem',
                  background: 'rgba(59, 130, 246, 0.05)',
                  borderRadius: '1rem',
                  border: '2px dashed rgba(59, 130, 246, 0.3)'
                }}>
                  <Video size={64} style={{ marginBottom: '1rem', opacity: 0.5, color: '#64748b' }} />
                  <h3 style={{ color: '#f8fafc', marginBottom: '0.5rem' }}>No Recordings</h3>
                  <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>Start recording or upload videos</p>
                  <button onClick={() => setShowUpload(true)} style={{ ...styles.button, ...styles.buttonSuccess }}>
                    <Upload size={20} />
                    Upload Video
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analysis */}
        {activeTab === 'analysis' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <div>
                <h2 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0, color: '#f8fafc' }}>
                  {analysisMode === 'traffic' ? 'Traffic Infringement Reports' : 'Security Analysis Results'}
                </h2>
                <p style={{ color: '#94a3b8', margin: '0.5rem 0 0 0' }}>
                  {analysisMode === 'traffic' ? 'Review traffic infringement analysis under NRTA 1996' : 'Review security analysis reports'}
                </p>
              </div>
            </div>

            {analysisResults.filter(r => analysisMode === 'traffic' ? r.analysisType === 'traffic' : r.analysisType !== 'traffic').length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '4rem 2rem',
                background: 'rgba(59, 130, 246, 0.05)',
                borderRadius: '1rem',
                border: '2px dashed rgba(59, 130, 246, 0.3)'
              }}>
                {analysisMode === 'traffic' ? (
                  <>
                    <Car size={64} style={{ marginBottom: '1rem', opacity: 0.5, color: '#64748b' }} />
                    <h3 style={{ color: '#f8fafc', marginBottom: '0.5rem' }}>No Traffic Reports</h3>
                    <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>Upload traffic footage to generate infringement reports</p>
                  </>
                ) : (
                  <>
                    <AlertTriangle size={64} style={{ marginBottom: '1rem', opacity: 0.5, color: '#64748b' }} />
                    <h3 style={{ color: '#f8fafc', marginBottom: '0.5rem' }}>No Analysis Results</h3>
                    <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>Upload videos to generate security reports</p>
                  </>
                )}
                <button onClick={() => setActiveTab('recordings')} style={{ ...styles.button, ...styles.buttonPrimary }}>
                  <Upload size={20} />
                  Upload Video for Analysis
                </button>
              </div>
            ) : (
              <div>
                {analysisResults.filter(r => analysisMode === 'traffic' ? r.analysisType === 'traffic' : r.analysisType !== 'traffic').map(result => {
                  const isTrafficResult = result.analysisType === 'traffic';
                  const badgeLabel = isTrafficResult
                    ? (result.classification || 'TRAFFIC_REPORT')
                    : result.threatLevel;
                  const badgeSuffix = !isTrafficResult && result.threatLevel !== 'NONE' ? ' THREAT' : '';
                  const displayColor = getThreatColor(isTrafficResult ? (result.classification || 'TRAFFIC_REPORT') : result.threatLevel);
                  const reportIcon = isTrafficResult ? Car : Shield;
                  const reportIconColor = isTrafficResult ? '#f59e0b' : '#60a5fa';
                  const reportTitle = isTrafficResult ? 'AI Traffic Infringement Report' : 'AI Security Analysis Report';
                  const reportTypeLabel = isTrafficResult ? 'Traffic Enforcement' : 'Security Analysis';

                  return (
                  <div
                    key={result.id}
                    ref={el => {
                      if (selectedAnalysis?.id === result.id && el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    style={{
                      background: selectedAnalysis?.id === result.id
                        ? `linear-gradient(135deg, ${displayColor}33, ${displayColor}1a)`
                        : 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(59, 130, 246, 0.05))',
                      borderRadius: '1rem',
                      padding: '2rem',
                      border: selectedAnalysis?.id === result.id
                        ? `2px solid ${displayColor}`
                        : '1px solid rgba(59, 130, 246, 0.3)',
                      marginBottom: '1.5rem',
                      transition: 'border 0.3s, background 0.3s'
                    }}
                  >
                    {/* Header with threat level / classification and metadata */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{
                          padding: '0.5rem 1rem',
                          borderRadius: '2rem',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          backgroundColor: displayColor,
                          color: 'white'
                        }}>
                          {badgeLabel}{badgeSuffix}
                        </span>
                        {(result.analysisType === 'traffic') && result.infringementsCount > 0 && (
                          <span style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '2rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: 'rgba(239, 68, 68, 0.15)',
                            color: '#fca5a5',
                            border: '1px solid rgba(239, 68, 68, 0.3)'
                          }}>
                            {result.majorCount} MAJOR
                          </span>
                        )}
                        {(result.analysisType === 'traffic') && result.minorCount > 0 && (
                          <span style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '2rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: 'rgba(245, 158, 11, 0.15)',
                            color: '#fcd34d',
                            border: '1px solid rgba(245, 158, 11, 0.3)'
                          }}>
                            {result.minorCount} MINOR
                          </span>
                        )}
                        <div>
                          <h3 style={{ color: '#f8fafc', margin: 0, fontSize: '1.25rem' }}>
                            {result.incidentType}
                          </h3>
                          <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                            <div>{result.cameraName} • {result.location}</div>
                            <div>{result.timestamp.toLocaleString()}</div>
                            {result.analysisMethod && (
                              <div style={{ color: (result.analysisType === 'traffic') ? '#f59e0b' : '#60a5fa', fontSize: '0.75rem' }}>
                                {result.analysisMethod} • {result.analysisDisplayType || result.analysisType} • {result.videoSize}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Report Display */}
                    <div style={{
                      padding: '2rem',
                      backgroundColor: 'rgba(15, 23, 42, 0.8)',
                      borderRadius: '0.75rem',
                      border: `1px solid ${displayColor}33`
                    }}>
                      <h4 style={{
                        color: '#e2e8f0',
                        marginBottom: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '1.1rem',
                        fontWeight: '600'
                      }}>
                        {React.createElement(reportIcon, { size: 20, color: reportIconColor })}
                        {reportTitle}
                      </h4>

                      {/* Traffic-specific summary */}
                      {(result.analysisType === 'traffic') && result.summary && (
                        <div style={{
                          padding: '1rem 1.5rem',
                          marginBottom: '1.5rem',
                          borderRadius: '0.5rem',
                          background: 'rgba(245, 158, 11, 0.1)',
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          color: '#fcd34d',
                          fontSize: '0.875rem',
                          fontWeight: '600'
                        }}>
                          <span style={{ marginRight: '0.5rem' }}>�</span>
                          {result.summary}
                        </div>
                      )}

                      <div
                        style={{ fontSize: '0.9rem', lineHeight: 1.6, color: '#d1d5db', whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: formatAnalysisReport(result.rawResponse) }}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div style={{
                      padding: '1.5rem 2rem',
                      borderTop: '1px solid rgba(59, 130, 246, 0.2)',
                      display: 'flex',
                      gap: '1rem',
                      flexWrap: 'wrap'
                    }}>
                      {(result.videoUrl || result.recordingId) && (
                        <button
                          onClick={() => {
                            if (result.recordingId) {
                              const linkedRecording = recordings.find(rec => rec.id === result.recordingId);
                              if (linkedRecording) {
                                setPlayingRecording(linkedRecording);
                                return;
                              }
                            }
                            if (result.videoUrl) {
                              const mockRecording = {
                                name: `${reportTypeLabel} Video - ${result.timestamp.toLocaleDateString()}`,
                                downloadURL: result.videoUrl,
                                blob: null,
                                camera: result.cameraName,
                                timestamp: result.timestamp
                              };
                              setPlayingRecording(mockRecording);
                            }
                          }}
                          style={{
                            ...styles.button,
                            ...styles.buttonSuccess,
                            fontSize: '0.875rem'
                          }}
                        >
                          <PlayCircle size={16} />
                          View Video
                        </button>
                      )}

                      {!result.videoUrl && !result.recordingId && (
                        <button
                          onClick={() => {
                            const matchingRecording = recordings.find(rec => {
                              const timeDiff = Math.abs(rec.timestamp.getTime() - result.timestamp.getTime());
                              return timeDiff < 5 * 60 * 1000;
                            });
                            if (matchingRecording) {
                              setPlayingRecording(matchingRecording);
                            } else {
                              addNotification('warning', 'No video available for this analysis.');
                            }
                          }}
                          style={{
                            ...styles.button,
                            backgroundColor: '#6b7280',
                            color: 'white',
                            fontSize: '0.875rem'
                          }}
                        >
                          <Camera size={16} />
                          Find Video
                        </button>
                      )}

                      <button
                        onClick={() => {
                          if (result.videoUrl) {
                            analyzeVideo(result.videoUrl, '', result.videoFileName || 'Re-analysis');
                          } else {
                            const matchingRecording = recordings.find(rec => {
                              const timeDiff = Math.abs(rec.timestamp.getTime() - result.timestamp.getTime());
                              return timeDiff < 5 * 60 * 1000;
                            });
                            if (matchingRecording) {
                              analyzeVideo(matchingRecording.blob || matchingRecording.downloadURL, '', matchingRecording.name);
                            } else {
                              addNotification('warning', 'Original video not found for re-analysis. Upload the video again to analyze.');
                            }
                          }
                        }}
                        disabled={isAnalyzing}
                        style={{
                          ...styles.button,
                          ...styles.buttonWarning,
                          fontSize: '0.875rem',
                          opacity: isAnalyzing ? 0.5 : 1
                        }}
                      >
                        {isAnalyzing ? <div style={styles.loadingSpinner}></div> : <AlertTriangle size={16} />}
                        Re-analyze
                      </button>

                      <button
                        onClick={() => {
                          const reportContent = `VIIS ${reportTypeLabel} Report
Generated: ${result.timestamp.toLocaleString()}
${(result.analysisType === 'traffic')
  ? `Classification: ${result.classification || 'Report'}
Infringements: ${result.infringementsCount || 0} (${result.majorCount || 0} Major, ${result.minorCount || 0} Minor)`
  : `Threat Level: ${result.threatLevel}`}
Camera: ${result.cameraName}
Location: ${result.location}
Analysis Method: ${result.analysisMethod}

${result.rawResponse}`;

                          const blob = new Blob([reportContent], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `VIIS_${(result.analysisType === 'traffic') ? 'Traffic' : 'Analysis'}_Report_${result.timestamp.toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
                          a.click();
                          URL.revokeObjectURL(url);
                          addNotification('success', 'Report downloaded');
                        }}
                        style={{
                          ...styles.button,
                          ...styles.buttonPrimary,
                          fontSize: '0.875rem'
                        }}
                      >
                        <Download size={16} />
                        Download Report
                      </button>
                    </div>
                  </div>
                )})}
              </div>
            )}
          </div>
        )}

        {/* Settings */}
        {activeTab === 'settings' && (
          <div>
            <h2 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '2rem', color: '#f8fafc' }}>
              System Settings
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              <div style={styles.card}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#f8fafc' }}>
                  <Settings size={28} color="#60a5fa" />
                  System Status
                </h3>
                <div style={{ fontSize: '0.875rem', color: '#d1d5db', lineHeight: 1.6 }}>
                  <p><strong>Version:</strong> VIIS v3.0 Enhanced</p>
                  <p><strong>Supabase:</strong> Connected</p>
                  <p><strong>Cameras:</strong> {cameras.length}</p>
                  <p><strong>Recordings:</strong> {recordings.length}</p>
                  <p><strong>Analysis Results:</strong> {analysisResults.length}</p>
                  <p><strong>HLS Support:</strong> {Hls.isSupported() ? 'Yes' : 'No'}</p>
                  <p><strong>AI Engine:</strong> VIIS Intelligence</p>
                  <p><strong>BulkSMS:</strong> Configured</p>
                </div>
              </div>

              <div style={styles.card}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#f8fafc' }}>
                  <Phone size={28} color="#10b981" />
                  Notification Testing
                </h3>
                <div style={{ fontSize: '0.875rem', color: '#d1d5db', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                  <p>Test SMS notifications for the selected camera.</p>
                  {selectedCamera ? (
                    <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                      <p><strong>Selected Camera:</strong> {selectedCamera.name}</p>
                      <p><strong>Location:</strong> {selectedCamera.location || 'Not set'}</p>
                      {selectedCamera.smsNotifications && (
                        <p><strong>SMS Recipients:</strong> {selectedCamera.smsNotifications.split(',').length} number(s)</p>
                      )}
                      {selectedCamera.emailNotifications && (
                        <p><strong>Email Recipients:</strong> {selectedCamera.emailNotifications.split(',').length} address(es)</p>
                      )}
                    </div>
                  ) : (
                    <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.5rem', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                      <p style={{ color: '#f59e0b' }}>⚠️ No camera selected. Please select a camera from the dashboard or cameras tab first.</p>
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={testSMSNotification}
                    disabled={!selectedCamera || !selectedCamera.smsNotifications}
                    style={{
                      ...styles.button,
                      ...styles.buttonSuccess,
                      flex: 1,
                      opacity: (!selectedCamera || !selectedCamera.smsNotifications) ? 0.5 : 1
                    }}
                  >
                    <Phone size={18} />
                    Test SMS
                  </button>
                </div>
              </div>

              <div style={styles.card}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#f8fafc' }}>
                  <Shield size={28} color="#ef4444" />
                  Threat Classification
                </h3>
                <div style={{ fontSize: '0.875rem', color: '#d1d5db', lineHeight: 1.6 }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#7c2d12' }}></div>
                      <strong style={{ color: '#f87171' }}>EXTREME:</strong>
                    </div>
                    <p style={{ marginLeft: '1rem', margin: '0 0 0.5rem 1rem' }}>Weapons, violence, explosives, active threats</p>
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#dc2626' }}></div>
                      <strong style={{ color: '#fca5a5' }}>HIGH:</strong>
                    </div>
                    <p style={{ marginLeft: '1rem', margin: '0 0 0.5rem 1rem' }}>Theft, robbery, hijacking, vandalism, break-ins</p>
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#f59e0b' }}></div>
                      <strong style={{ color: '#fbbf24' }}>MEDIUM:</strong>
                    </div>
                    <p style={{ marginLeft: '1rem', margin: '0 0 0.5rem 1rem' }}>Suspicious behavior, loitering, unauthorized access</p>
                  </div>
                  
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#22c55e' }}></div>
                      <strong style={{ color: '#86efac' }}>NONE:</strong>
                    </div>
                    <p style={{ marginLeft: '1rem', margin: '0 0 0.5rem 1rem' }}>No security incidents detected</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Add Camera Modal */}
      {showAddCamera && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '2rem', color: '#f8fafc' }}>
              Add New Camera
            </h3>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#e2e8f0' }}>
                Camera Name *
              </label>
              <input
                type="text"
                value={newCamera.name}
                onChange={(e) => setNewCamera({...newCamera, name: e.target.value})}
                style={styles.input}
                placeholder="e.g., Main Entrance, Parking Lot"
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#e2e8f0' }}>
                RTSP Stream URL *
              </label>
              <input
                type="text"
                value={newCamera.rtspUrl}
                onChange={(e) => setNewCamera({...newCamera, rtspUrl: e.target.value})}
                style={styles.input}
                placeholder="rtsp://camera-ip:port/stream"
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#e2e8f0' }}>
                Location
              </label>
              <input
                type="text"
                value={newCamera.location}
                onChange={(e) => setNewCamera({...newCamera, location: e.target.value})}
                style={styles.input}
                placeholder="e.g., OUTDOORS, ENTRANCE, PARKING"
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: '#e2e8f0' }}>
                SMS Notifications
              </label>
              <input
                type="text"
                value={newCamera.smsNotifications}
                onChange={(e) => setNewCamera({...newCamera, smsNotifications: e.target.value})}
                style={styles.input}
                placeholder="+27123456789, +27987654321"
              />
              <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                Enter phone numbers with country code, separated by commas. SMS alerts sent for HIGH/EXTREME threats.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button
                onClick={() => setShowAddCamera(false)}
                style={{ ...styles.button, backgroundColor: '#374151', color: 'white', flex: 1 }}
              >
                Cancel
              </button>
              <button onClick={addCamera} style={{ ...styles.button, ...styles.buttonPrimary, flex: 1 }}>
                <Camera size={18} />
                Add Camera
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div style={styles.modal}>
          <div style={{...styles.modalContent, maxWidth: '40rem'}}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '2rem', color: '#f8fafc' }}>
              Upload Video for Analysis
            </h3>

            {/* File Upload Section */}
            <div style={{
              border: '3px dashed rgba(59, 130, 246, 0.5)',
              borderRadius: '1rem',
              padding: '2rem',
              textAlign: 'center',
              cursor: 'pointer',
              background: 'rgba(59, 130, 246, 0.05)',
              marginBottom: '1.5rem'
            }}>
              <Upload size={48} color="#60a5fa" style={{ margin: '0 auto 1rem auto', display: 'block' }} />
              <h4 style={{ color: '#f8fafc', marginBottom: '0.5rem' }}>Upload from Computer</h4>
              <p style={{ color: '#94a3b8', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                Supports MP4, WebM, AVI, MOV formats
              </p>

              <input
                type="file"
                accept="video/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="video-upload"
              />
              <label htmlFor="video-upload" style={{ ...styles.button, ...styles.buttonPrimary, cursor: 'pointer' }}>
                <Upload size={18} />
                Select Video File
              </label>
            </div>

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ flex: 1, height: '1px', background: 'rgba(59, 130, 246, 0.3)' }} />
              <span style={{ color: '#94a3b8', fontSize: '0.875rem', fontWeight: 'bold' }}>OR</span>
              <div style={{ flex: 1, height: '1px', background: 'rgba(59, 130, 246, 0.3)' }} />
            </div>

            {/* YouTube URL Section */}
            <div style={{
              border: '2px solid rgba(239, 68, 68, 0.4)',
              borderRadius: '1rem',
              padding: '1.5rem',
              background: 'rgba(239, 68, 68, 0.05)',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <Youtube size={24} color="#ef4444" />
                <h4 style={{ color: '#f8fafc', margin: 0, fontSize: '1rem' }}>Analyze from YouTube</h4>
              </div>
              <p style={{ color: '#94a3b8', marginBottom: '1rem', fontSize: '0.8rem' }}>
                Paste a YouTube video URL to download and analyze for security threats
              </p>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <Link size={16} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="text"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleYoutubeAnalyze()}
                    style={{
                      width: '100%',
                      padding: '0.75rem 0.75rem 0.75rem 2.25rem',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      background: 'rgba(0, 0, 0, 0.4)',
                      color: '#f8fafc',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <button
                  onClick={handleYoutubeAnalyze}
                  disabled={isDownloadingYoutube || !youtubeUrl.trim()}
                  style={{
                    ...styles.button,
                    backgroundColor: '#ef4444',
                    color: 'white',
                    whiteSpace: 'nowrap',
                    opacity: (isDownloadingYoutube || !youtubeUrl.trim()) ? 0.5 : 1,
                    cursor: (isDownloadingYoutube || !youtubeUrl.trim()) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isDownloadingYoutube ? (
                    <>
                      <Loader size={16} className="loading-spinner" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Youtube size={16} />
                      Analyze
                    </>
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={() => { setShowUpload(false); setYoutubeUrl(''); }}
              style={{ ...styles.button, backgroundColor: '#374151', color: 'white', width: '100%' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {playingRecording && (
        <div style={styles.modal}>
          <div style={{...styles.modalContent, maxWidth: '50rem', width: '90vw'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f8fafc', margin: 0 }}>
                {playingRecording.name}
              </h3>
              <button
                onClick={() => setPlayingRecording(null)}
                style={{ ...styles.button, backgroundColor: '#374151', color: 'white', padding: '0.5rem' }}
              >
                <XCircle size={20} />
              </button>
            </div>
            
            <div style={{ backgroundColor: '#000', borderRadius: '0.75rem', overflow: 'hidden', marginBottom: '1.5rem' }}>
              <video
                src={playingRecording.downloadURL || (playingRecording.blob ? URL.createObjectURL(playingRecording.blob) : '')}
                controls
                autoPlay
                style={{ width: '100%', height: 'auto', maxHeight: '60vh' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => analyzeVideo(playingRecording.blob || playingRecording.downloadURL, '', playingRecording.name)}
                disabled={isAnalyzing}
                style={{ 
                  ...styles.button, 
                  ...styles.buttonWarning, 
                  flex: 1,
                  opacity: isAnalyzing ? 0.5 : 1
                }}
              >
                {isAnalyzing ? <div style={styles.loadingSpinner}></div> : <AlertTriangle size={18} />}
                Analyze Video
              </button>
              <button
                onClick={() => downloadRecording(playingRecording)}
                style={{ ...styles.button, ...styles.buttonPrimary, flex: 1 }}
              >
                <Download size={18} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      <div style={styles.notifications}>
        {notifications.map(notification => {
          const getIcon = () => {
            switch (notification.type) {
              case 'success': return <CheckCircle size={18} />;
              case 'error': return <XCircle size={18} />;
              case 'warning': return <AlertTriangle size={18} />;
              default: return <Info size={18} />;
            }
          };

          const getColor = () => {
            switch (notification.type) {
              case 'success': return 'linear-gradient(135deg, rgba(34, 197, 94, 0.9), rgba(16, 185, 129, 0.8))';
              case 'error': return 'linear-gradient(135deg, rgba(239, 68, 68, 0.9), rgba(220, 38, 38, 0.8))';
              case 'warning': return 'linear-gradient(135deg, rgba(245, 158, 11, 0.9), rgba(217, 119, 6, 0.8))';
              default: return 'linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(37, 99, 235, 0.8))';
            }
          };

          return (
            <div
              key={notification.id}
              style={{
                ...styles.notification,
                background: getColor()
              }}
            >
              {getIcon()}
              <span style={{ flex: 1 }}>{notification.message}</span>
            </div>
          );
        })}
      </div>

      {/* AI Analysis Overlay — blocks all interaction during analysis */}
      {isAnalyzing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'wait',
        }}>
          {/* Spinner */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            border: '4px solid rgba(59, 130, 246, 0.2)',
            borderTopColor: '#3b82f6',
            animation: 'spin 0.8s linear infinite',
            marginBottom: '2rem',
          }} />

          <h2 style={{
            color: '#f8fafc',
            fontSize: '1.5rem',
            fontWeight: '700',
            margin: '0 0 0.75rem 0',
          }}>
            AI Analysis in Progress
          </h2>

          <p style={{
            color: '#94a3b8',
            fontSize: '1rem',
            margin: '0 0 2rem 0',
            textAlign: 'center',
            maxWidth: '30rem',
          }}>
            {analysisMode === 'traffic'
              ? 'Analyzing traffic footage for infringements under NRTA 1996...'
              : 'Analyzing video footage for security threats...'}
          </p>

          {/* Progress steps */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            maxWidth: '24rem',
            width: '100%',
          }}>
            {[
              { label: 'Preparing video frames', done: true },
              { label: 'Sending footage to AI engine', done: true },
              { label: 'AI analyzing footage', done: false },
              { label: 'Processing analysis results', done: false },
            ].map((step, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                background: step.done
                  ? 'rgba(34, 197, 94, 0.1)'
                  : i === 2
                    ? 'rgba(59, 130, 246, 0.15)'
                    : 'rgba(255, 255, 255, 0.05)',
                border: step.done
                  ? '1px solid rgba(34, 197, 94, 0.3)'
                  : i === 2
                    ? '1px solid rgba(59, 130, 246, 0.4)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                {step.done ? (
                  <CheckCircle size={18} color="#22c55e" />
                ) : i === 2 ? (
                  <div style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: '2px solid rgba(59, 130, 246, 0.4)',
                    borderTopColor: '#3b82f6',
                    animation: 'spin 1s linear infinite',
                  }} />
                ) : (
                  <div style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.15)',
                  }} />
                )}
                <span style={{
                  color: step.done ? '#86efac' : i === 2 ? '#93c5fd' : '#64748b',
                  fontSize: '0.875rem',
                }}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          <p style={{
            color: '#64748b',
            fontSize: '0.75rem',
            marginTop: '2rem',
          }}>
            Please wait — do not close or refresh this page
          </p>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default AuthWrapper;